import { useEffect, useRef, useState } from 'react';

const Contact = () => {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });
    setFormStatus('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d764tu5r5g02gnd69r7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      color: '#00d4ff',
      label: 'Call Us',
      value: '(813) 952-6893',
      href: 'tel:+18139526893',
    },
    {
      icon: 'ri-whatsapp-line',
      color: '#39ff14',
      label: 'WhatsApp',
      value: '(813) 952-6893',
      href: 'https://wa.me/18139526893',
    },
    {
      icon: 'ri-mail-line',
      color: '#b026ff',
      label: 'Email Us',
      value: 'info@martled.com',
      href: 'mailto:info@martled.com',
    },
  ];

  const inputClasses = `w-full px-4 py-3.5 rounded-xl bg-black/40 text-white text-sm font-body placeholder-gray-600 outline-none transition-all duration-300 focus:border-neon-blue`;
  const inputStyle = { border: '1px solid rgba(0,212,255,0.2)' };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-dark-base overflow-hidden" ref={sectionRef}>
      {/* BG Grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(0,212,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />
      {/* Diagonal Streaks */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute w-px h-full opacity-10 rotate-12"
          style={{ left: '20%', background: 'linear-gradient(to bottom, transparent, #39ff14, transparent)' }}
        />
        <div
          className="absolute w-px h-full opacity-10 -rotate-12"
          style={{ right: '20%', background: 'linear-gradient(to bottom, transparent, #00d4ff, transparent)' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left Content */}
          <div
            className={`flex-1 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div
              className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
              style={{ color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 10px rgba(57,255,20,0.3)' }}
            >
              Let&apos;s Talk
            </div>

            <h2 className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">
              <span className="text-white">READY TO</span>
              <br />
              <span style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14' }}>BE SEEN?</span>
            </h2>
            <p className="text-gray-400 text-xl font-rajdhani font-medium mb-6">
              Let&apos;s put your brand on the road
            </p>

            <div className="w-28 h-1 rounded-full mb-10" style={{ backgroundColor: '#39ff14', boxShadow: '0 0 15px #39ff14' }} />

            {/* Contact Methods */}
            <div className="space-y-4 mb-8">
              {contactMethods.map((method) => (
                <a
                  key={method.label}
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-2xl bg-dark-card transition-all duration-300 hover:scale-102 group cursor-pointer"
                  style={{ border: `1px solid ${method.color}20` }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = method.color;
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = `0 0 20px ${method.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = `${method.color}20`;
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
                  }}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: `${method.color}15` }}>
                    <i className={`${method.icon} text-xl`} style={{ color: method.color, textShadow: `0 0 10px ${method.color}` }} />
                  </div>
                  <div>
                    <div className="text-xs font-rajdhani tracking-widest uppercase text-gray-500 mb-0.5">{method.label}</div>
                    <div className="font-rajdhani font-bold text-white text-base group-hover:text-opacity-90 transition-colors">{method.value}</div>
                  </div>
                  <i className="ri-arrow-right-up-line ml-auto text-gray-600 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              {['Licensed & Insured', 'Tampa Based', 'Fast Response'].map((badge) => (
                <div
                  key={badge}
                  className="px-4 py-2 rounded-full text-xs font-rajdhani font-bold"
                  style={{ backgroundColor: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.2)', color: '#39ff14' }}
                >
                  <i className="ri-verified-badge-line mr-1.5" />
                  {badge}
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div
            className={`flex-1 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div
              className="p-8 rounded-3xl bg-dark-card"
              style={{
                border: '2px solid transparent',
                background: 'linear-gradient(#0f0f0f, #0f0f0f) padding-box, linear-gradient(135deg, #00d4ff, #b026ff) border-box',
                boxShadow: '0 0 60px rgba(0,212,255,0.1)',
              }}
            >
              <h3 className="font-orbitron font-bold text-2xl text-white mb-2">Quick Quote Request</h3>
              <p className="text-gray-500 text-sm font-body mb-8">We respond within 2 hours during business hours</p>

              {formStatus === 'success' ? (
                <div
                  className="p-8 rounded-2xl text-center"
                  style={{ backgroundColor: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.3)' }}
                >
                  <i className="ri-check-double-line text-5xl mb-4 block" style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14' }} />
                  <h4 className="font-orbitron font-bold text-xl text-white mb-2">Message Sent!</h4>
                  <p className="text-gray-400 text-sm">We&apos;ll get back to you shortly. Get ready to be seen!</p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      required
                      className={inputClasses}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      required
                      className={inputClasses}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      className={inputClasses}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <select
                      name="business_type"
                      className={`${inputClasses} text-gray-400`}
                      style={inputStyle}
                      defaultValue=""
                    >
                      <option value="" disabled>Business Type</option>
                      <option value="law_firm">Law Firm</option>
                      <option value="realtor">Realtor / Real Estate</option>
                      <option value="car_dealership">Car Dealership</option>
                      <option value="restaurant">Restaurant / Food</option>
                      <option value="event">Event / Promotion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <select
                      name="campaign_interest"
                      className={`${inputClasses} text-gray-400`}
                      style={inputStyle}
                      defaultValue=""
                    >
                      <option value="" disabled>Campaign Interest</option>
                      <option value="monthly_starter">Monthly Starter - $750/mo</option>
                      <option value="monthly_pro">Monthly Professional - $1,500/mo</option>
                      <option value="hourly">Hourly Campaign - $150/hr</option>
                      <option value="custom">Custom Campaign</option>
                    </select>
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your campaign goals... (max 500 characters)"
                      rows={4}
                      maxLength={500}
                      className={`${inputClasses} resize-none`}
                      style={inputStyle}
                    />
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-red-400 text-xs font-rajdhani">Something went wrong. Please try again or call us directly.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-4 rounded-full font-rajdhani font-black tracking-widest uppercase text-base text-black transition-all duration-300 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{ backgroundColor: '#39ff14', boxShadow: '0 0 30px rgba(57,255,20,0.4)' }}
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" /> Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Get Your Free Quote <i className="ri-arrow-right-line" />
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
